import React, { useEffect, useRef, useState } from "react";
import "./ImportEmployeeModal.css"; // External CSS file
import arrowIcon from "../../../../assets/images/arrowIcon.svg";
import { Form, Formik } from "formik";
import { notifySuccess, notifyWarn } from "../../../../assets/elements/Toast";
import * as Yup from "yup";
import CustomErrorMessage from "../../../../assets/common/CustomeErrorMessage";
import MainServices from "../../../../services/MainServices";
import ButtonLoading from "../../../../assets/loaders/ButtonLoading";
import { useTranslation } from "react-i18next";
import useConfig from "../../../../hooks/useConfig";
import { Info } from "lucide-react";
import ValidationModal from "./ValidationModal";
import DeleteConfirmationModal from "../table/DeleteModal";
import Tick from "../../../../assets/customIcons/Tick";
import ExclaimRed from "../../../../assets/customIcons/ExclaimRed";
import StatusModal from "../common/StatusModal";

export default function ImportEmployeeModal({
  isOpen,
  onClose,
  getTableData,
  getAdditionalCounts,
}) {
  const config = useConfig();
  const fileInputRef = useRef(null);
  const { importEmployeeUpload, getStatus } = MainServices();
  const [loading, setLoading] = useState(false);
  const { t, i18n } = useTranslation();
  const [isOpenValidation, setIsOpenValidaion] = useState(false);
  let importTimeoutRef = useRef(null);
  const [refereshKey, setRefreshKey] = useState(1);
  const [headlineMsg, setHeadlineMsg] = useState("");
  const [paraMsg, setParaMsg] = useState("");
  const [buttonTxt, setButtonTxt] = useState("");
  const [notifyModal, setNotifyModal] = useState(false);
  const [modalIcon, setModalIcon] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    return () => {
      if (importTimeoutRef.current) {
        clearTimeout(importTimeoutRef.current);
      }
    };
  }, []);

  function onCloseValidation() {
    setIsOpenValidaion(false);
  }

  function handleOpenFileInput() {
    fileInputRef.current.click();
  }

  const validationSchema = Yup.object({
    impFile: Yup.mixed()
      .required("File is required")
      .test("fileType", "Only .zip, .xls, .xlsx files are allowed", (value) => {
        if (!value) return false;
        const allowedExtensions = [".zip", ".xls", ".xlsx"];
        const fileExtension = `.${value.name.split(".").pop().toLowerCase()}`;
        return allowedExtensions.includes(fileExtension);
      })
      .test("fileSize", "File size must be less than 50MB", (value) => {
        return value && value.size <= 50 * 1024 * 1024; // 50MB limit
      }),
  });

  const getUploadStatus = async (jobId, resetForm) => {
    try {
      const res = await getStatus({}, jobId);
      const data = res?.data?.data;
      const status = data?.import_status;
      const uploadStatus = data?.upload_status;
      if (status === "completed") {
        if (uploadStatus === "completed") {
          clearTimeout(importTimeoutRef.current);
          setRefreshKey((prevKey) => prevKey + 1);
          setLoading(false);
          setIsSuccess(true);
          setModalIcon(<Tick />);

          setHeadlineMsg("Import Successful!");
          setParaMsg("Employees have been imported successfully.");
          setButtonTxt("Close");
          setNotifyModal(true);
          getTableData();
          getAdditionalCounts();
          resetForm();
        } else if (uploadStatus === "failed") {
          clearTimeout(importTimeoutRef.current);
          setRefreshKey((prevKey) => prevKey + 1);
          setLoading(false);
          setIsSuccess(false);

          setModalIcon(<ExclaimRed />);
          setHeadlineMsg("Import Failed");
          setParaMsg("Please check your email for details about the failure.");
          setButtonTxt("Close");
          setNotifyModal(true);
        } else {
          setRefreshKey((prevKey) => prevKey + 1);
          importTimeoutRef.current = setTimeout(() => {
            getUploadStatus(jobId, resetForm);
          }, 3000);
        }
      } else if (status === "failed") {
        clearTimeout(importTimeoutRef.current);
        setRefreshKey((prevKey) => prevKey + 1);
        setLoading(false);
        setIsSuccess(false);

        setModalIcon(<ExclaimRed />);
        setHeadlineMsg("Something Went Wrong");
        setParaMsg(
          "An unexpected error occurred during the import. Please try again."
        );
        setButtonTxt("Close");
        setNotifyModal(true);
      } else {
        setRefreshKey((prevKey) => prevKey + 1);
        importTimeoutRef.current = setTimeout(() => {
          getUploadStatus(jobId, resetForm);
        }, 3000);
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || "";
      // errorMessage && notifyWarn(`${errorMessage}`);

      console.error("Error Importing Employee:", error);
      setIsSuccess(false);

      setModalIcon(<ExclaimRed />);
      setHeadlineMsg("Employees Import Failed!");
      setParaMsg(errorMessage ? errorMessage : "An unexpected error occurred");
      setButtonTxt("Try again");
      setNotifyModal(true);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const handleButtonClick = () => {
    setNotifyModal(false);
    if (isSuccess) {
      onClose();
    }
  };

  const handleSubmit = async (values, resetForm) => {
    setLoading(true);
    const uploadFiles = [values?.impFile];
    try {
      const response = await importEmployeeUpload(uploadFiles);
      if (response?.data) {
        console.log("Import Created Successfully", response);
        // onClose();
        // resetForm();
        let jobId = response?.data?.data?.jobId;
        setLoading(true);
        importTimeoutRef.current = setTimeout(() => {
          getUploadStatus(jobId, resetForm);
        }, 1000);
      }
    } catch (error) {
      notifyWarn(
        error?.response?.data?.message || "An unexpected error occurred"
      );
      setLoading(false);
      console.error("Error", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadSample = (isZip = false) => {
    try {
      const exportFileName = isZip
        ? config?.exportFileNameZip
        : config?.exportFileNameExcel;
      const exportLink = config?.exportLink;

      if (!exportFileName || !exportLink) {
        notifyWarn(
          t("Export file or link is missing. Please try again later.")
        );
        return;
      }

      const fileLink = `${exportLink}${exportFileName}`;

      const link = document.createElement("a");
      link.href = fileLink;
      link.download = exportFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      notifySuccess(t("File Exported Successfully"));
    } catch (error) {
      console.error("Download error:", error);
      notifyWarn(t("Failed to download file"));
    }
  };

  if (!isOpen) return null;

  console.log("notifymodal: ", notifyModal);

  return (
    <>
      <div
        className={`imp-modal-overlay ${notifyModal ? "hide-modal" : ""}`}
        onClick={onClose}
      >
        <Formik
          initialValues={{ impFile: null }}
          validationSchema={validationSchema}
          onSubmit={(values, { resetForm }) => {
            handleSubmit(values, resetForm);
          }}
        >
          {({ values, errors, touched, setFieldValue }) => (
            <Form
              onClick={(e) => e.stopPropagation()}
              className="imp-modal-content"
            >
              <div className="imp-modal-header">
                <span className="imp-modal-title">{t("Import Employee")}</span>
                <div className="imp-modal-icon-container" onClick={onClose}>
                  <img src={arrowIcon} alt="arrow icon" />
                </div>
              </div>
              <div className="imp-body">
                <p className="imp-desc">
                  {t("Please select the import file from your device")}
                </p>
                <div className="imp-file-input-container">
                  <div className="imp-file-input-container2">
                    <div className="imp-label-container">
                      <label htmlFor="impFile" className="imp-label">
                        {t("Select File")}
                      </label>
                      <span className="imp-req-mark">*</span>
                    </div>
                    <div
                      className="imp-file-input-wrapper"
                      onClick={handleOpenFileInput}
                    >
                      <span className="imp-file-input-replacer px-2">
                        {values?.impFile
                          ? values.impFile.name
                          : t("Select Employee File")}
                      </span>
                      <input
                        name="impFile"
                        ref={fileInputRef}
                        id="impFile"
                        type="file"
                        className="imp-file-hidden-input"
                        accept=".zip,.xls,.xlsx"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          console.log("Selected file:", file);
                          setFieldValue("impFile", file);
                        }}
                      />
                      <button type="button" className="imp-btn px-2">
                        {t("Upload File")}
                      </button>
                    </div>
                    <CustomErrorMessage
                      name="impFile"
                      errors={errors}
                      touched={touched}
                    />
                  </div>
                </div>
                <div className="flex justify-end mt-2">
                  <button
                    onClick={() => setIsOpenValidaion(true)}
                    className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-indigo-700 bg-white border border-indigo-300 rounded-lg hover:bg-indigo-50 hover:border-indigo-400 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 active:scale-95 transition-all duration-200 shadow-sm"
                  >
                    <Info className="w-4 h-4" />
                    {t("Review Validation Rules")}
                  </button>
                </div>
              </div>
              <div className="flex flex-col gap-3 justify-end pt-6 pr-6 pl-6 pb-1 bg-gray-50 border-t border-gray-200">
                <button
                  type="button"
                  className="self-end inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-blue-700 bg-white border border-blue-300 rounded-lg hover:bg-blue-50 hover:border-blue-400 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 active:scale-95 transition-all duration-200 shadow-sm"
                  onClick={() => handleDownloadSample(false)}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  {t("Download Sample Excel File")}
                </button>
                <button
                  type="button"
                  className="self-end inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-green-700 bg-white border border-green-300 rounded-lg hover:bg-green-50 hover:border-green-400 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 active:scale-95 transition-all duration-200 shadow-sm"
                  onClick={() => handleDownloadSample(true)}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 10v6m0 0l-3-3m3 3l3-3M4 7l4-4 4 4M8 3v18"
                    />
                  </svg>
                  {t("Download Sample Zip File")}
                </button>
              </div>
              <div className="imp-footer">
                <button
                  type="submit"
                  disabled={loading}
                  className="imp-footer-btn"
                >
                  {loading ? <ButtonLoading /> : t("Import Employee")}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>

      {isOpenValidation && (
        <ValidationModal
          isOpen={isOpenValidation}
          onClose={onCloseValidation}
        />
      )}
      {notifyModal && (
        <StatusModal
          icon={modalIcon}
          isOpen={notifyModal}
          headline={headlineMsg}
          paraText={paraMsg}
          confirmText={buttonTxt}
          isSuccess={isSuccess} // true for success (green), false for failed (red)
          onConfirm={handleButtonClick}
          onClose={handleButtonClick}
        />
      )}
      {/* {notifyModal && (
        <DeleteConfirmationModal
          icon={modalIcon}
          isOpen={notifyModal}
          headline={headlineMsg}
          paraText={paraMsg}
          confirmText={buttonTxt}
          isConfirm={isSuccess}
          isDelete={isSuccess}
          onConfirm={handleButtonClick}
          onCancel={handleButtonClick}
          onClose={handleButtonClick}
        />
      )} */}

      {/* {errorModal && (
        <DeleteConfirmationModal
          icon={<errorModal />}
          isOpen={reactivateModal}
          headline={headlineMsg}
          paraText={paraMsg}
          buttonText={buttonTxt}
          confirmText={}
          isDelete={true}
          isConfirm={isSuc}
          onConfirm={handleReactivateConfirm}
          onCancel={handleReactivateConfirm}
          onClose={handleReactivateConfirm}
        />
      )} */}
    </>
  );
}
